import { useMutation, useQueryClient } from 'react-query'
import { AlmanacFileCalendarItem } from './types'
import { calendarKeys } from './calendarKeys'
import { sendToApi } from 'api/client'
import {
  setupMutation,
  updateUnscheduledItems,
  processDateRangeQueries,
  createItemProcessor,
  handleMutationError,
} from './utils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

interface CreateCalendarItemParams {
  unscheduledItem?: AlmanacFileCalendarItem
  postAt: string
  status?: string
  id: string
}

export function useCreateCalendarItem(projectKey: string) {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const projectKeys = [projectKey]

  return useMutation(
    ({ id, postAt, status }: CreateCalendarItemParams) => {
      return sendToApi(`/w/${workspaceId}/calendar_items`, {
        method: 'POST',
        body: { id, calendar_item: { post_at: postAt, status } },
      })
    },
    {
      onMutate: async ({ unscheduledItem, postAt }: CreateCalendarItemParams) => {
        if (!unscheduledItem) return
        const context = await setupMutation(queryClient, { projectKeys })

        const newItem: AlmanacFileCalendarItem = {
          ...unscheduledItem,
          post_at: postAt,
        }

        updateUnscheduledItems(queryClient, { projectKeys }, 'remove', unscheduledItem, context.previousData)
        processDateRangeQueries(queryClient, { projectKeys }, createItemProcessor(newItem), context.previousData)

        return context
      },
      onError: (error, variables, context) => handleMutationError(queryClient, error, variables, context),
      onSettled: () => queryClient.invalidateQueries(calendarKeys.filters({ projectKeys })),
    },
  )
}
